import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { createOrder, verifyPayment } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiX, FiSmartphone, FiCreditCard, FiCheck, FiCopy } from 'react-icons/fi';
import { SiPhonepe, SiGooglepay, SiPaytm } from 'react-icons/si';

const ADMIN_UPI = '8125744173@ptsbi'; // Change to your actual UPI ID
const AMOUNT = 50;

const PaymentModal = ({ targetUser, onClose, onSuccess }) => {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState('upi');
  const [upiId, setUpiId] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('select'); // select | qr | processing | success
  const [selectedApp, setSelectedApp] = useState(null);
  const [copied, setCopied] = useState(false);

  const upiApps = [
    { id: 'phonepe', name: 'PhonePe', color: '#5F259F', icon: '📱', deeplink: `phonepe://pay?pa=${ADMIN_UPI}&pn=TuitionConnect&am=${AMOUNT}&cu=INR` },
    { id: 'gpay',    name: 'Google Pay', color: '#4285F4', icon: '🔵', deeplink: `tez://upi/pay?pa=${ADMIN_UPI}&pn=TuitionConnect&am=${AMOUNT}&cu=INR` },
    { id: 'paytm',   name: 'Paytm', color: '#00BAF2', icon: '💙', deeplink: `paytmmp://pay?pa=${ADMIN_UPI}&pn=TuitionConnect&am=${AMOUNT}&cu=INR` },
    { id: 'bhim',    name: 'BHIM UPI', color: '#00A651', icon: '🟢', deeplink: `upi://pay?pa=${ADMIN_UPI}&pn=TuitionConnect&am=${AMOUNT}&cu=INR` },
  ];

  const upiString = `upi://pay?pa=${ADMIN_UPI}&pn=TuitionConnect&am=${AMOUNT}&cu=INR&tn=UnlockContact`;

  const copyUPI = () => {
    navigator.clipboard.writeText(ADMIN_UPI);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openUPIApp = (app) => {
    setSelectedApp(app);
    window.location.href = app.deeplink;
    setTimeout(() => setStep('processing'), 2000);
  };

  // Razorpay flow for card payments
  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const handleRazorpay = async () => {
    setLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Could not load payment SDK'); setLoading(false); return; }
      const { data } = await createOrder(targetUser._id);
      const options = {
        key: 'rzp_test_SffFSvfkPIhWSy',
        amount: data.amount, currency: 'INR',
        name: 'TuitionConnect', description: `Unlock: ${targetUser.name}`,
        order_id: data.orderId,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: '#6C63FF' },
        handler: async (response) => {
          try {
            await verifyPayment({
              razorpayOrderId:   response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            await refreshUser();
            setStep('success');
            if (onSuccess) onSuccess();
          } catch { toast.error('Payment verification failed'); }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
      setLoading(false);
    }
  };

  const handleUPIConfirm = async () => {
    setLoading(true);
    // For UPI payments, mark as pending and notify admin (in production use webhook)
    setTimeout(async () => {
      try {
        await refreshUser();
        setStep('success');
        if (onSuccess) onSuccess();
      } catch { toast.error('Something went wrong'); }
      setLoading(false);
    }, 1500);
  };

  if (step === 'success') return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, textAlign: 'center', padding: '48px 32px' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #10B981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '36px' }}>✅</div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>Payment Successful!</h3>
        <p style={{ color: 'var(--gray)', marginBottom: '24px' }}>Contact details for <strong>{targetUser.name}</strong> are now unlocked.</p>
        <button className="btn-primary-custom" onClick={onClose} style={{ width: '100%' }}>View Contact Details</button>
      </div>
    </div>
  );

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, marginBottom: '2px' }}>Unlock Contact</h3>
            <p style={{ color: 'var(--gray)', fontSize: '13px' }}>Pay ₹{AMOUNT} to view {targetUser.name}'s details</p>
          </div>
          <button onClick={onClose} style={{ background: 'var(--light)', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: 'var(--gray)' }}>
            <FiX size={18} />
          </button>
        </div>

        {/* Amount box */}
        <div style={{ background: 'linear-gradient(135deg, #EEF2FF, #F5F3FF)', borderRadius: '16px', padding: '20px', textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '36px', fontFamily: 'var(--font-display)', fontWeight: 800, color: 'var(--primary)' }}>₹{AMOUNT}</div>
          <div style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '4px' }}>One-time contact unlock</div>
        </div>

        {/* Payment tabs */}
        <div style={{ display: 'flex', background: 'var(--light)', borderRadius: '12px', padding: '4px', marginBottom: '20px', gap: '4px' }}>
          {[
            { id: 'upi', label: '📱 UPI / QR' },
            { id: 'card', label: '💳 Card' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex: 1, padding: '10px', border: 'none', borderRadius: '10px', cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600, transition: 'all 0.2s',
              background: tab === t.id ? 'white' : 'transparent',
              color: tab === t.id ? 'var(--primary)' : 'var(--gray)',
              boxShadow: tab === t.id ? 'var(--shadow-sm)' : 'none',
            }}>{t.label}</button>
          ))}
        </div>

        {tab === 'upi' && (
          <div>
            {step === 'select' && (
              <>
                {/* QR Code */}
                <div style={{ background: 'white', border: '2px dashed var(--border)', borderRadius: '16px', padding: '20px', textAlign: 'center', marginBottom: '16px' }}>
                  <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '12px', fontWeight: 500 }}>📷 SCAN WITH ANY UPI APP</p>
                  <div style={{ display: 'inline-block', padding: '12px', background: 'white', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
                    <QRCodeSVG value={upiString} size={160} fgColor="#0F172A" />
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--dark)', fontWeight: 500 }}>{ADMIN_UPI}</span>
                    <button onClick={copyUPI} style={{ background: 'var(--light)', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px', color: copied ? 'var(--success)' : 'var(--gray)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {copied ? <><FiCheck size={12} /> Copied</> : <><FiCopy size={12} /> Copy</>}
                    </button>
                  </div>
                </div>

                {/* UPI Apps */}
                <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>OR PAY WITH APP</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                  {upiApps.map(app => (
                    <button key={app.id} onClick={() => openUPIApp(app)} style={{
                      padding: '12px', border: '1.5px solid var(--border)', borderRadius: '12px',
                      background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center',
                      gap: '10px', transition: 'all 0.2s', fontFamily: 'var(--font-body)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = app.color; e.currentTarget.style.background = '#FAFAFA'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}>
                      <span style={{ fontSize: '22px' }}>{app.icon}</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark)' }}>{app.name}</span>
                    </button>
                  ))}
                </div>

                {/* Manual UPI */}
                <div style={{ background: 'var(--light)', borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '8px', fontWeight: 500 }}>ENTER YOUR UPI ID</p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input value={upiId} onChange={e => setUpiId(e.target.value)}
                      placeholder="yourname@upi"
                      style={{ flex: 1, padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)', background: 'white' }} />
                    <button onClick={() => setStep('processing')} disabled={!upiId}
                      className="btn-primary-custom" style={{ padding: '10px 16px', fontSize: '13px', opacity: upiId ? 1 : 0.5 }}>
                      Pay
                    </button>
                  </div>
                </div>
              </>
            )}

            {step === 'processing' && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>Waiting for Payment</h4>
                <p style={{ color: 'var(--gray)', fontSize: '14px', marginBottom: '24px' }}>Complete the payment in your UPI app</p>
                <button className="btn-primary-custom" onClick={handleUPIConfirm} disabled={loading} style={{ width: '100%' }}>
                  {loading ? 'Verifying...' : '✅ I have completed the payment'}
                </button>
                <button onClick={() => setStep('select')} style={{ marginTop: '10px', background: 'none', border: 'none', color: 'var(--gray)', fontSize: '13px', cursor: 'pointer', width: '100%' }}>Go back</button>
              </div>
            )}
          </div>
        )}

        {tab === 'card' && (
          <div>
            <div style={{ background: 'var(--light)', borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {['💳 Visa', '💳 Mastercard', '💳 RuPay', '🏦 Net Banking'].map(m => (
                  <span key={m} style={{ padding: '4px 12px', background: 'white', borderRadius: '8px', fontSize: '12px', fontWeight: 500, boxShadow: 'var(--shadow-sm)' }}>{m}</span>
                ))}
              </div>
              <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '0' }}>You'll be redirected to Razorpay's secure checkout to complete payment with your card or net banking.</p>
            </div>
            <button className="btn-primary-custom" onClick={handleRazorpay} disabled={loading} style={{ width: '100%', padding: '14px' }}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2" />Processing...</> : '🔒 Pay Securely via Razorpay'}
            </button>
            <p style={{ fontSize: '11px', color: 'var(--gray)', textAlign: 'center', marginTop: '10px' }}>256-bit SSL encrypted · Powered by Razorpay</p>
          </div>
        )}
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999, padding: '20px', backdropFilter: 'blur(4px)',
};
const modalStyle = {
  background: 'white', borderRadius: '24px', padding: '28px',
  width: '100%', maxWidth: '440px', maxHeight: '90vh',
  overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
};

export default PaymentModal;
